import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import Input from './ui/Input';
import useLanguage from '../hooks/useLanguage';

const IndicatorSettingsModal = ({ isOpen, onClose, availableIndicators, selectedIndicators, onSave }) => {
  const [localSelected, setLocalSelected] = useState(selectedIndicators || []);
  const [settings, setSettings] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeIndicator, setActiveIndicator] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen) {
      setLocalSelected(selectedIndicators || []);
      
      // Initialize settings from availableIndicators
      const initialSettings = {};
      availableIndicators.forEach(indicator => {
        if (indicator.defaultSettings) {
          initialSettings[indicator.key] = { ...indicator.defaultSettings };
        }
      });
      setSettings(initialSettings);
      
      // Set first indicator as active
      if (availableIndicators.length > 0) {
        setActiveIndicator(availableIndicators[0]);
      }
    }
  }, [isOpen, selectedIndicators, availableIndicators]);

  const toggleIndicator = (indicatorKey) => {
    setLocalSelected(prev => 
      prev.includes(indicatorKey)
        ? prev.filter(key => key !== indicatorKey)
        : [...prev, indicatorKey]
    );
  };

  const updateSetting = (indicatorKey, settingKey, value) => {
    setSettings(prev => ({
      ...prev,
      [indicatorKey]: {
        ...prev[indicatorKey],
        [settingKey]: value
      }
    }));
  };

  const handleSave = () => {
    onSave(localSelected, settings);
    onClose();
  };

  const handleClear = () => {
    setLocalSelected([]);
    setSearchQuery('');
  };

  const filteredIndicators = availableIndicators.filter(ind =>
    ind.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ind.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-2xl w-full max-w-5xl h-[85vh] overflow-hidden border border-border flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-bold text-foreground">{t('indicators') || 'Göstergeler'}</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-muted rounded transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Two Panel Layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Indicator List */}
          <div className="w-80 border-r border-border flex flex-col bg-muted/20">
            {/* Search Bar */}
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={t('search') || 'Ara'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-9 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-[#23827D] focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="X" size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Indicator List */}
            <div className="flex-1 overflow-y-auto">
              {filteredIndicators.map((indicator) => {
                const isSelected = localSelected.includes(indicator.key);
                const isActive = activeIndicator?.key === indicator.key;

                return (
                  <button
                    key={indicator.key}
                    onClick={() => setActiveIndicator(indicator)}
                    className={`w-full px-4 py-3 text-left border-b border-border/50 transition-colors ${
                      isActive
                        ? 'bg-[#23827D]/10 border-l-4 border-l-[#23827D]'
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-foreground truncate">
                          {indicator.name}
                        </div>
                      </div>
                      {isSelected && (
                        <Icon name="Check" size={16} className="text-[#23827D] flex-shrink-0 ml-2" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right Panel - Indicator Settings */}
          <div className="flex-1 flex flex-col bg-background">
            {activeIndicator ? (
              <>
                {/* Indicator Title */}
                <div className="p-4 border-b border-border">
                  <h3 className="text-lg font-bold text-foreground">{activeIndicator.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{activeIndicator.description}</p>
                </div>

                {/* Settings Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-6">
                    {/* Toggle Indicator */}
                    <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                      <div>
                        <div className="font-medium text-foreground">{t('enableIndicator') || 'İndikatörü Etkinleştir'}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {localSelected.includes(activeIndicator.key) ? t('enabled') || 'Aktif' : t('disabled') || 'Pasif'}
                        </div>
                      </div>
                      <button
                        onClick={() => toggleIndicator(activeIndicator.key)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          localSelected.includes(activeIndicator.key) ? 'bg-[#23827D]' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            localSelected.includes(activeIndicator.key) ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Parameter Settings */}
                    {activeIndicator.settings && localSelected.includes(activeIndicator.key) && (
                      <div className="space-y-4">
                        <div className="font-medium text-foreground text-sm">{t('parameters') || 'Parametreler'}</div>
                        {activeIndicator.settings.map((setting) => {
                          const currentValue = settings[activeIndicator.key]?.[setting.key] || setting.default;
                          
                          return (
                            <div key={setting.key} className="space-y-2">
                              <label className="text-sm font-medium text-foreground">
                                {setting.label}
                              </label>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => updateSetting(activeIndicator.key, setting.key, Math.max(setting.min, currentValue - (setting.step || 1)))}
                                  className="w-8 h-8 flex items-center justify-center border border-border rounded hover:bg-muted transition-colors"
                                >
                                  <Icon name="Minus" size={14} />
                                </button>
                                <Input
                                  type="number"
                                  value={currentValue}
                                  onChange={(e) => updateSetting(activeIndicator.key, setting.key, parseFloat(e.target.value))}
                                  className="flex-1 text-center"
                                  min={setting.min}
                                  max={setting.max}
                                  step={setting.step || 1}
                                />
                                <button
                                  onClick={() => updateSetting(activeIndicator.key, setting.key, Math.min(setting.max, currentValue + (setting.step || 1)))}
                                  className="w-8 h-8 flex items-center justify-center border border-border rounded hover:bg-muted transition-colors"
                                >
                                  <Icon name="Plus" size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}

                        {/* Color Preview */}
                        <div className="space-y-2 pt-4 border-t border-border">
                          <label className="text-sm font-medium text-foreground">{t('color') || 'Renk'}</label>
                          <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                            <div
                              className="w-12 h-12 rounded border-2 border-border shadow-sm"
                              style={{ backgroundColor: activeIndicator.color }}
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-foreground">{activeIndicator.name}</div>
                              <div className="text-xs text-muted-foreground font-mono">{activeIndicator.color}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <Icon name="BarChart3" size={48} className="mx-auto mb-3 opacity-50" />
                  <p>{t('selectIndicator') || 'Bir indikatör seçin'}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {localSelected.length} {t('indicatorsSelected') || 'indikatör seçildi'}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClear}
              className="px-4 py-2"
            >
              {t('clear') || 'Temizle'}
            </Button>
            <Button
              onClick={handleSave}
              className="px-4 py-2 bg-[#23827D] hover:bg-[#23827D]/90 text-white"
            >
              {t('add') || 'Ekle'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndicatorSettingsModal;

